import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useLogout} from '../queries';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {loggedInUserSelector} from '../../../../../store/selectors/auth.selectors';
// eslint-disable-next-line max-len
import DropdownMenuCompactHeader from '../../../../../incase-domain/components/dropdownMenuCompact/header/profile/DropdownMenuCompactProfileHeader';
import Button from '../../../../../incase-domain/components/button/Button';
import ConfirmModal from '../../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {Role} from '../../../../../incase-domain/user';

type RoleOptionTranslationKey =
    | 'registerPage.roleOptions.superuser'
    | 'registerPage.roleOptions.admin'
    | 'registerPage.roleOptions.presenter'
    | 'registerPage.roleOptions.creator'
    | 'registerPage.roleOptions.viewer';

const ROLE_TRANSLATION_KEY: Record<Role, RoleOptionTranslationKey | undefined> = {
    [Role.SUPERUSER]: 'registerPage.roleOptions.superuser',
    [Role.ADMIN]: 'registerPage.roleOptions.admin',
    [Role.PRESENTER]: 'registerPage.roleOptions.presenter',
    [Role.CREATOR]: 'registerPage.roleOptions.creator',
    [Role.VIEWER]: 'registerPage.roleOptions.viewer',
    [Role.USER]: undefined,
};

function ProfileHeaderItem({theme}: {theme: 'light' | 'dark'}): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const user = useSelector(loggedInUserSelector);
    const logout = useLogout();

    const [modal, setModal] = useState<boolean>(false);

    const onLogoutConfirm = (): void => {
        logout();
    };

    const role = user?.roles[0];
    const roleTranslationKey = role ? ROLE_TRANSLATION_KEY[role] : undefined;

    return (
        <>
            <DropdownMenuCompact
                header={
                    <DropdownMenuCompactHeader
                        title={user?.email || 'Anonymous'}
                        subtitle={roleTranslationKey ? t(roleTranslationKey) : ''}
                    />
                }
                items={[
                    [
                        {
                            label: t('header.profile.settings'),
                            icon: 'solar:settings-linear',
                            onClick: () => navigate('/settings'),
                        },
                        {
                            label: t('header.profile.logOut'),
                            icon: 'solar:logout-3-linear',
                            onClick: () => setModal(true),
                            dataCy: 'logout',
                        },
                    ],
                ]}
                menuPosition={DropdownMenuCompactMenuPosition.LEFT}
            >
                <Button
                    leading="solar:user-linear"
                    color={theme === 'light' ? 'basic' : 'primary'}
                    size="md"
                    variant="ghost"
                    square
                    className="c-header__actions_icon"
                    dataCy="userProfile"
                />
            </DropdownMenuCompact>

            <ConfirmModal
                message={t('sidebar.logoutText')}
                icon="solar:logout-3-bold"
                isOpen={modal}
                onCancel={() => setModal(false)}
                onConfirm={onLogoutConfirm}
                confirmText={t('sidebar.logoutConfirm')}
                cancelText={t('sidebar.cancel')}
                showCancel
                showConfirm
                dataCy="logoutModal"
            />
        </>
    );
}

export default ProfileHeaderItem;
